import React from "react";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import { graphql } from "gatsby";
import Layout from "../components/Layout";
import Content, { HTMLContent } from "../components/Content";
import { Row, Col } from "reactstrap";
import SuccessStory from "../components/SuccessStory";
import ShareButtons from "../components/ShareButtons";
import SocialButtons from "../components/SocialButtons";
import Taglist from "../components/Taglist";
import NewsHeadline from "../components/NewsHeadline";
import Img from "gatsby-image";
import styles from "../templates/style.module.scss";
import { ImageCard as Card } from "@jobmatchme/ui";

export const BlogPostTemplate = ({
  content,
  contentComponent,
  description,
  tags,
  title,
  helmet,
  date,
  pathname,
  teaserImage
}) => {
  const PostContent = contentComponent || Content;
  return (
    <>
      {helmet || ""}

      <NewsHeadline pathname={pathname} />

      <Row>
        <Col xs={12} lg={9}>
          <Card headline={date}>
            <article>
              <header>
                <h1 className="title is-size-2 has-text-weight-bold is-bold-light">
                  {title}
                </h1>
              </header>
              <p>{description}</p>

              {teaserImage && (
                <figure className={styles.figure}>
                  <Img fluid={teaserImage.childImageSharp.fluid} alt={title} />
                  <figcaption>{title}</figcaption>
                </figure>
              )}

              <PostContent content={content} />
              {tags && tags.length ? <Taglist tags={tags} /> : null}
            </article>
          </Card>
          <section>
            <SuccessStory />
          </section>
        </Col>
        <Col xs={12} lg={3}>
          <aside>
            <Card headline="&nbsp;">
              <ShareButtons />
              <hr />
              <SocialButtons />
            </Card>
          </aside>
        </Col>
      </Row>
    </>
  );
};

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  helmet: PropTypes.object,
  teaserImage: PropTypes.object
};

const BlogPost = ({ data, location }) => {
  const { markdownRemark: post } = data;

  return (
    <Layout>
      <BlogPostTemplate
        content={post.html}
        contentComponent={HTMLContent}
        description={post.frontmatter.description}
        date={post.frontmatter.date}
        helmet={
          <Helmet titleTemplate="%s | Truck-Jobs Wissen">
            <title>{`${post.frontmatter.title}`}</title>
            <meta
              name="description"
              content={`${post.frontmatter.description} | Truck-Jobs`}
            />
          </Helmet>
        }
        tags={post.frontmatter.tags}
        title={post.frontmatter.title}
        pathname={location.pathname}
        teaserImage={post.frontmatter.teaserImage}
      />
    </Layout>
  );
};

BlogPost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object
  })
};

export default BlogPost;

export const pageQuery = graphql`
  query BlogPostByID($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      excerpt(pruneLength: 600)
      frontmatter {
        date(formatString: "DD. MMMM YYYY", locale: "de")
        title
        description
        tags
        path
        teaser
        teaserImage {
          childImageSharp {
            fluid(maxWidth: 761) {
              ...GatsbyImageSharpFluid_noBase64
            }
          }
        }
      }
    }
  }
`;
